import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from '@shared/services/notification.service';
import { IEnvironmentRepairNeeded, IEnvironmentRepairResponse } from '../sync-monitoring.models';
import { SyncMonitoringService } from '../sync-monitoring.service';

/** Dialog payload: the environment to repair and the repair-needed record the page showed (display-only; the dialog asks core-one itself what the repair would change). */
export interface IRepairEnvironmentDialogData {
	environmentId: string;
	environmentName: string;
	repairNeeded: IEnvironmentRepairNeeded;
}

// core-one repair outcomes (EnvironmentRepairOutcome). A dry run that would apply something reports
// Repaired (RepairNeeded when a caller only reports it); NoOp means there is nothing left to repair.
const APPLYABLE_OUTCOMES = new Set(['Repaired', 'RepairNeeded']);
const SUCCESSFUL_OUTCOMES = new Set(['Repaired', 'NoOp']);

/** Applies the repair core-one diagnoses for an environment, after showing what it would change. Opening the dialog runs POST .../repair?dryRun=true (writes nothing); Confirm runs the repair itself. Closes with `true` once the repair applied, so the page reloads the environment. */
@Component({
	selector: 'app-repair-environment-dialog',
	templateUrl: './repair-environment-dialog.component.html',
	styleUrls: ['./repair-environment-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class RepairEnvironmentDialogComponent implements OnInit {
	public isLoadingPreview = true;
	public isApplying = false;
	public preview: IEnvironmentRepairResponse | null = null;
	/** Set when the repair itself ran but did not apply cleanly; shown instead of the preview. */
	public result: IEnvironmentRepairResponse | null = null;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: IRepairEnvironmentDialogData,
		public dialogRef: MatDialogRef<RepairEnvironmentDialogComponent, boolean>,
		private syncMonitoringService: SyncMonitoringService,
		private notificationService: NotificationService,
	) {}

	ngOnInit(): void {
		this.syncMonitoringService.repairEnvironment(this.data.environmentId, true).subscribe({
			next: (preview) => {
				this.preview = preview;
				this.isLoadingPreview = false;
			},
			error: () => {
				this.isLoadingPreview = false;
				this.notificationService.notify(['administration.sync-monitoring.repair-dialog.errors.preview-failed'], [], 'error');
			},
		});
	}

	/** The dry run found a repair to apply. */
	get canApply(): boolean {
		return !this.isApplying && this.result === null && APPLYABLE_OUTCOMES.has(this.preview?.outcome ?? '');
	}

	/** The dry run found nothing left to repair (e.g. the state changed since the page loaded). */
	get hasNothingToRepair(): boolean {
		return this.preview?.outcome === 'NoOp';
	}

	/** What the dialog is currently describing: the repair's own result once it ran, else the dry run. */
	get shown(): IEnvironmentRepairResponse | null {
		return this.result ?? this.preview;
	}

	onConfirm(): void {
		if (!this.canApply) {
			return;
		}

		// Escape or a backdrop click would close the dialog before close(true), and the page would miss the reload.
		this.setApplying(true);
		this.syncMonitoringService.repairEnvironment(this.data.environmentId, false).subscribe({
			next: (result) => {
				if (SUCCESSFUL_OUTCOMES.has(result.outcome ?? '')) {
					this.dialogRef.close(true);
					return;
				}

				// Blocked, a retryable conflict or applied but still blocked: keep the dialog open and
				// show core-one's reasons, so the operator can fall back to resetting the tables.
				this.setApplying(false);
				this.result = result;
				this.notificationService.notify(['administration.sync-monitoring.repair-dialog.errors.not-applied'], [], 'error');
			},
			error: () => {
				this.setApplying(false);
				this.notificationService.notify(['administration.sync-monitoring.repair-dialog.errors.repair-failed'], [], 'error');
			},
		});
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	/** Only Cancel closes the dialog while the repair runs, and Cancel is disabled then. */
	private setApplying(applying: boolean): void {
		this.isApplying = applying;
		this.dialogRef.disableClose = applying;
	}
}
